import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * AddressAutocomplete Component
 * Uses Nominatim (OpenStreetMap) for free address autocomplete
 * No API key required
 */
export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect, // 🆕 NEW: Callback with structured address data
  placeholder = "Adresse eingeben...",
  className = "",
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch address suggestions from Nominatim
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    try {
      // Nominatim API - Free, no API key needed
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=5&` +
        `countrycodes=de`, // Limit to Germany
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'AllFlowX/1.0' // Required by Nominatim
          }
        }
      );

      if (!response.ok) throw new Error('API Error');

      const data = await response.json();
      
      // Format suggestions
      const formatted = data.map(item => ({
        display_name: item.display_name,
        address: {
          road: item.address.road || '',
          house_number: item.address.house_number || '',
          postcode: item.address.postcode || '',
          city: item.address.city || item.address.town || item.address.village || '',
          state: item.address.state || ''
        },
        lat: item.lat,
        lon: item.lon
      }));

      setSuggestions(formatted);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for API call (respect rate limit)
    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 500); // 500ms debounce
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    // 🆕 Call onAddressSelect with structured address data
    if (onAddressSelect) {
      onAddressSelect({
        street: suggestion.address.road || '',
        house_number: suggestion.address.house_number || '',
        postal_code: suggestion.address.postcode || '',
        city: suggestion.address.city || '',
        full_address: suggestion.display_name,
        coordinates: {
          lat: suggestion.lat,
          lon: suggestion.lon
        }
      });
    }
    
    // Update the search input with full display name
    onChange(suggestion.display_name);
    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={`pl-10 pr-10 ${className}`}
          disabled={disabled}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                index === highlightedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {suggestion.address.road && suggestion.address.house_number
                      ? `${suggestion.address.road} ${suggestion.address.house_number}`
                      : suggestion.address.road || suggestion.display_name.split(',')[0]}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {suggestion.address.postcode && suggestion.address.city
                      ? `${suggestion.address.postcode} ${suggestion.address.city}`
                      : suggestion.display_name}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && !loading && suggestions.length === 0 && value.length >= 3 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-4 text-center text-sm text-gray-500">
          Keine Adressen gefunden
        </div>
      )}

      {/* Helper text */}
      {value.length > 0 && value.length < 3 && (
        <p className="text-xs text-gray-500 mt-1">
          Mindestens 3 Zeichen für Vorschläge
        </p>
      )}
    </div>
  );
};
